/**
 * Export/import of custom QC presets to a file (cue-forge-4o9): pure,
 * framework-free logic so the file-shape, forward-compat, conflict, and cap rules
 * are all TDD'd without React or `localStorage`. Lives in the **lazy** editor chunk
 * (only `CustomPresetEditor` imports it), so none of this reaches the eager bundle.
 *
 * The export file IS the store's `localStorage` envelope shape (`{ version,
 * presets }`) plus a `kind` provenance marker, so {@link parseCustomPresets} — the
 * store's tolerant, security-hardened parser (forces `verified:false`, strips
 * `reference`, rejects `custom:`-prefix / built-in-id shadows) — is reused verbatim
 * for import. Nothing here touches the network; import is entirely local.
 */

import {
  CUSTOM_PRESETS_VERSION,
  MAX_CUSTOM_PRESETS,
  parseCustomPresets,
  sanitizeCustomRuleset,
  type CustomPresetEntry,
} from './customPresets';
import { isBuiltinPresetName } from './presetLabels';
import type { Ruleset } from '../../core';

/** Provenance marker in an exported file (distinguishes our file from random JSON). */
export const EXPORT_KIND = 'cueforge-qc-presets';

interface ExportEnvelope {
  kind: string;
  version: number;
  presets: CustomPresetEntry[];
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Serialize entries to a pretty, hand-readable export file (bundle or single). */
export function buildExportEnvelope(entries: readonly CustomPresetEntry[]): string {
  const envelope: ExportEnvelope = {
    kind: EXPORT_KIND,
    version: CUSTOM_PRESETS_VERSION,
    presets: [...entries],
  };
  return JSON.stringify(envelope, null, 2);
}

/** A filesystem-safe slug of a preset name, or `''` when nothing survives. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Download filename: single files carry the preset's slug, bundles are generic. */
export function exportFilename(entries: readonly CustomPresetEntry[]): string {
  if (entries.length === 1) {
    return `cueforge-qc-preset-${slugify(entries[0]!.ruleset.name) || 'preset'}.json`;
  }
  return 'cueforge-qc-presets.json';
}

/** Why an import file could not be read (each maps to a distinct user message). */
export type ImportParseFailure = 'not-json' | 'not-our-file' | 'future-version' | 'empty';

/** The outcome of reading an import file: the validated presets, or a reason. */
export type ImportParseResult =
  | { ok: true; presets: CustomPresetEntry[] }
  | { ok: false; reason: ImportParseFailure };

/**
 * Read + validate an import file. Distinguishes a corrupt/foreign file from a
 * newer-version file from an ours-but-empty file, so the UI shows a precise
 * message. Delegates the per-entry coercion to {@link parseCustomPresets} (one
 * validation path — the imported presets inherit the store's Draft/no-reference
 * hardening), after peeking at `version` to catch a forward-incompatible file that
 * `parseCustomPresets` would otherwise silently reduce to `[]`.
 */
export function parseImportFile(text: string): ImportParseResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, reason: 'not-json' };
  }
  if (!isObject(parsed)) return { ok: false, reason: 'not-our-file' };

  const { version } = parsed;
  const looksOurs =
    parsed.kind === EXPORT_KIND || (typeof version === 'number' && Array.isArray(parsed.presets));
  if (!looksOurs) return { ok: false, reason: 'not-our-file' };
  if (typeof version === 'number' && version > CUSTOM_PRESETS_VERSION) {
    return { ok: false, reason: 'future-version' };
  }

  const presets = parseCustomPresets(text);
  if (presets.length === 0) return { ok: false, reason: 'empty' };
  return { ok: true, presets };
}

/** The existing preset an incoming name would collide with (case-insensitive, trimmed). */
export function nameConflict(
  name: string,
  existing: readonly CustomPresetEntry[],
): CustomPresetEntry | undefined {
  const needle = name.trim().toLowerCase();
  return existing.find((e) => e.ruleset.name.trim().toLowerCase() === needle);
}

/** How the operator resolved one importable preset (per-conflict prompt, spec §4). */
export type Resolution = 'keep-both' | 'overwrite' | 'skip' | { rename: string };

/** One imported preset with its detected conflict and the operator's resolution. */
export interface ResolvedImport {
  incoming: CustomPresetEntry;
  /** The existing same-named preset, or `null` when there is no conflict. */
  conflict: CustomPresetEntry | null;
  resolution: Resolution;
}

/** Append a fresh preset (id already minted). */
export interface AddOp {
  type: 'add';
  entry: CustomPresetEntry;
}
/** Replace an existing preset's ruleset in place (keeps its id + createdAt). */
export interface OverwriteOp {
  type: 'overwrite';
  targetId: string;
  ruleset: Ruleset;
  updatedAt: number;
}
export type ImportOp = AddOp | OverwriteOp;

/**
 * Turn resolved imports into store operations. Every add mints a **fresh** id
 * (`mint`) so an import never aliases or silently overwrites a preset by id — only
 * an explicit `overwrite` touches an existing entry, and only by its id. `keep-both`
 * / `rename` and any non-conflict become adds; `overwrite` without a conflict
 * degrades to an add; `skip` yields nothing. Pure: the clock (`now`) and id source
 * (`mint`) are injected.
 */
export function buildImportOps(
  items: readonly ResolvedImport[],
  mint: () => string,
  now: number,
): ImportOp[] {
  const ops: ImportOp[] = [];
  for (const { incoming, conflict, resolution } of items) {
    if (resolution === 'skip') continue;
    if (resolution === 'overwrite' && conflict) {
      ops.push({
        type: 'overwrite',
        targetId: conflict.id,
        ruleset: incoming.ruleset,
        updatedAt: now,
      });
      continue;
    }
    const name = typeof resolution === 'object' ? resolution.rename : incoming.ruleset.name;
    ops.push({
      type: 'add',
      entry: {
        ...incoming,
        id: mint(),
        createdAt: now,
        updatedAt: now,
        ruleset: { ...incoming.ruleset, name },
      },
    });
  }
  return ops;
}

/**
 * Counts of what an import did, surfaced to the operator after applying. The
 * skips are split by reason (cue-forge-drt.19): an operator's explicit "Skip"
 * resolution reads very differently from a preset the cap refused or an entry
 * that couldn't be written, so the summary must not collapse them.
 */
export interface ImportSummary {
  added: number;
  updated: number;
  /** "Skip" resolutions the operator chose in the review panel. */
  skippedByUser: number;
  /** Adds refused because the store already held `maxPresets` presets. */
  skippedCap: number;
  /** Everything else: invalid/spoofed ruleset, vanished overwrite target. */
  skippedOther: number;
}

/** The operator-chosen "Skip" count for a set of resolved imports (drt.19). */
export function countUserSkips(items: readonly ResolvedImport[]): number {
  return items.filter((it) => it.resolution === 'skip').length;
}

/**
 * Fold import ops onto the current list, returning the next list + a summary. Adds
 * that would exceed `maxPresets` are skipped (no silent truncation — the caller
 * surfaces the count, with the cap as its reason); an overwrite whose target is
 * gone is skipped. Every written ruleset is re-run through
 * {@link sanitizeCustomRuleset} (defence in depth: a hand-edited file can never
 * smuggle in `verified:true` or a `reference`); an op whose ruleset fails
 * validation is skipped rather than aborting the whole import. An op whose name
 * spoofs a built-in preset is skipped too (cue-forge-drt.11) — the same guard the
 * save path enforces, so an import file can never plant a second "Netflix
 * (Latin)" in the pickers. Operator "Skip" resolutions never reach the ops
 * ({@link buildImportOps} drops them) — callers count those with
 * {@link countUserSkips}, so `skippedByUser` is always 0 here.
 */
export function applyImport(
  existing: readonly CustomPresetEntry[],
  ops: readonly ImportOp[],
  maxPresets: number = MAX_CUSTOM_PRESETS,
): { next: CustomPresetEntry[]; summary: ImportSummary } {
  const next = [...existing];
  const summary: ImportSummary = {
    added: 0,
    updated: 0,
    skippedByUser: 0,
    skippedCap: 0,
    skippedOther: 0,
  };
  for (const op of ops) {
    try {
      const name = op.type === 'add' ? op.entry.ruleset.name : op.ruleset.name;
      if (isBuiltinPresetName(name)) {
        summary.skippedOther++;
        continue;
      }
      if (op.type === 'add') {
        if (next.length >= maxPresets) {
          summary.skippedCap++;
          continue;
        }
        next.push({ ...op.entry, ruleset: sanitizeCustomRuleset(op.entry.ruleset) });
        summary.added++;
      } else {
        const idx = next.findIndex((e) => e.id === op.targetId);
        if (idx < 0) {
          summary.skippedOther++;
          continue;
        }
        next[idx] = {
          ...next[idx]!,
          ruleset: sanitizeCustomRuleset(op.ruleset),
          updatedAt: op.updatedAt,
        };
        summary.updated++;
      }
    } catch {
      summary.skippedOther++;
    }
  }
  return { next, summary };
}

/**
 * The operator-facing one-liner for an import result (drt.19). Skips are broken
 * out by reason and omitted entirely when zero, e.g.
 * "Imported: 2 added, 1 updated, 2 skipped by you, 1 skipped: preset cap reached."
 */
export function importSummaryMessage(s: ImportSummary): string {
  const parts = [`${s.added} added`, `${s.updated} updated`];
  if (s.skippedByUser > 0) parts.push(`${s.skippedByUser} skipped by you`);
  if (s.skippedCap > 0) parts.push(`${s.skippedCap} skipped: preset cap reached`);
  if (s.skippedOther > 0) parts.push(`${s.skippedOther} skipped: couldn't be imported`);
  return `Imported: ${parts.join(', ')}.`;
}
